<template>
  <div
      class="group relative overflow-hidden rounded-2xl border border-white/10
         bg-slate-950/40 backdrop-blur-2xl p-5
         ring-1 ring-amber-400/10 hover:ring-amber-400/15 transition"
  >
    <div class="pointer-events-none absolute -inset-24 opacity-35 blur-3xl
              bg-gradient-to-r
              from-amber-600/25 via-yellow-300/15 to-transparent"/>

    <div class="pointer-events-none absolute inset-0
              bg-[radial-gradient(circle_at_20%_15%,rgba(255,215,160,0.18),transparent_45%)]"/>

    <div class="pointer-events-none absolute inset-0
              bg-[linear-gradient(to_bottom,rgba(255,240,200,0.10),transparent_35%)]"/>    <!-- Header -->
    <div class="flex items-center justify-between mb-3">
      <div class="flex items-center gap-3 min-w-0">
        <div
            class="w-10 h-10 rounded-xl bg-gray-900/50 border border-amber-900/20 flex items-center justify-center shrink-0"
            style="max-width: 40px">
          💰
        </div>
        <div class="min-w-0">
          <div class="text-sm font-extrabold text-slate-100 truncate">گزارش موجودی</div>
          <div class="text-[11px] text-slate-400 mt-0.5">
            <span v-if="lastUpdatedAt">آخرین بروزرسانی: {{ formatTime(lastUpdatedAt) }}</span>
            <span v-else>آخرین بروزرسانی: —</span>
          </div>
        </div>
      </div>
      <div class="justify-start mr-5">
        <button class="btn btn--ghost flex-shrink-0" @click="goToReport" :disabled="loading" title="نمایش بیشتر">
          <span class="text-xs white--text">نمایش بیشتر</span>
        </button>
      </div>

    </div>

    <!-- Slider -->
    <div class="ticker  p-2">
      <div v-if="loading" class="grid grid-cols-3 gap-2">
        <div v-for="i in 3" :key="'sk'+i" class="miniCard">
          <div class="h-4 w-24 bg-white/5 rounded mb-2"></div>
          <div class="h-6 w-32 bg-white/5 rounded mb-2"></div>
          <div class="h-4 w-24 bg-white/5 rounded"></div>
        </div>
      </div>

      <div v-else class="viewport" @mouseenter="pause" @mouseleave="resume">
        <div
            ref="track"
            class="track"
            :style="trackStyle"
        >
          <div
              v-for="(it, i) in items"
              :key="it.group + '_' + i"
              class="miniCard"
              :ref="i === 0 ? 'card0' : null"
          >
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-2 min-w-0">
                🪙
                <div class="font-extrabold text-slate-100 truncate max-w-[160px]">{{ it.group }}</div>
              </div>
            </div>

            <div class="mt-2">
              <div class="text-[11px] text-slate-400 mb-1">ارزش موجودی</div>
              <div class="text-lg font-black text-slate-100">
                {{ it.value | toMoney(0) }}
                <span class="text-[10px] text-slate-500 mr-1">{{ it.group }}</span>
              </div>
            </div>

            <div class="mt-2 flex items-center justify-between">
              <div class="text-[11px] text-slate-400">موجودی تعدادی</div>
              <div class="text-sm font-black text-slate-100">{{ formatInt(it.total_inventory) }}</div>
            </div>

            <div class="mt-2 h-2 rounded-full bg-white/5 border border-white/10 overflow-hidden">
              <div class="h-full rounded-full" :style="barStyle(it)"></div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!loading && items.length === 0" class="p-4 text-center text-slate-400 text-sm">
        داده‌ای یافت نشد
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "InventoryReportMiniSlider",
  data() {
    return {
      inventory: [],
      loading: false,
      lastUpdatedAt: null,

      // slider
      index: 0,
      timer: null,
      paused: false,
      stepPx: 0,
      gapPx: 0,

      // responsive
      cardsPerView: 3,
    };
  },
  computed: {
    items() {
      // گروه‌ها رو می‌چینیم بر اساس value (مثل صفحه اصلی)
      return this.normalized
          .slice()
          .sort((a, b) => b.value - a.value);
    },
    normalized() {
      const src = Array.isArray(this.inventory) ? this.inventory : [];
      return src.map(x => {
        const group = (x.group ?? "").toString();
        const total_inventory = Number(x.total_inventory || 0);
        const total_base = Number(x.total_base || 0);
        const total_shipping_cost = Number(x.total_shipping_cost || 0);
        const exchange = Number(x.exchange || 0);
        const value = total_base + total_shipping_cost;

        return {group, total_inventory, total_base, total_shipping_cost, exchange, value};
      });
    },
    totalValue() {
      return this.items.reduce((s, it) => s + (Number(it.value) || 0), 0);
    },
    trackStyle() {
      // RTL: برای رفتن جلو، track رو به سمت راست می‌بریم (translateX مثبت)
      const x = this.index * (this.stepPx || 0);
      return {
        transform: `translateX(${x}px)`,
      };
    },
  },
  created() {
    this.fetchInventories();
  },
  mounted() {
    this.updateCardsPerView();
    window.addEventListener("resize", this.updateCardsPerView);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateCardsPerView);
    this.stop();
  },
  methods: {
    // ---------- Data ----------
    fetchInventories() {
      this.loading = true;
      this.stop();
      this.request({
        url: this.endpoint("product/inventory/currency"),
        method: "get",
        success: (data) => {
          this.inventory = Array.isArray(data) ? data : [];
          this.lastUpdatedAt = new Date();
          this.loading = false;

          this.$nextTick(() => {
            this.measureStep();
            this.start();
          });
        },
        error: () => {
          this.loading = false;
          this.stop();
        },
      });
    },
    refresh() {
      this.fetchInventories();
    },

    // ---------- Slider logic ----------
    updateCardsPerView() {
      // ساده و کاربردی:
      // موبایل: 1
      // تبلت: 2
      // دسکتاپ: 3
      const w = window.innerWidth || 1200;
      this.cardsPerView = w < 640 ? 1 : (w < 1024 ? 2 : 3);
      this.$nextTick(this.measureStep);
    },
    measureStep() {
      // عرض کارت + gap را می‌گیریم تا translate دقیق باشد
      const card = this.$refs.card0;
      const track = this.$refs.track;
      if (!card || !track) return;

      const cardEl = Array.isArray(card) ? card[0] : card;
      const cs = window.getComputedStyle(track);
      const gap = parseFloat(cs.columnGap || cs.gap || "0") || 0;

      this.gapPx = gap;
      this.stepPx = cardEl.offsetWidth + gap;

      // اگر index از محدوده بیرون بود ریست
      const maxIndex = this.maxIndex();
      if (this.index > maxIndex) this.index = 0;
    },
    maxIndex() {
      const n = this.items.length || 0;
      return Math.max(0, n - this.cardsPerView);
    },
    start() {
      this.stop();
      if (!this.items.length) return;

      this.timer = setInterval(() => {
        if (this.paused) return;

        const maxI = this.maxIndex();
        if (maxI <= 0) {
          this.index = 0;
          return;
        }
        this.index = (this.index >= maxI) ? 0 : (this.index + 1);
      }, 2000);
    },
    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    },
    pause() {
      this.paused = true;
    },
    resume() {
      this.paused = false;
    },

    // ---------- Navigation ----------
    goToReport() {
      if (this.$router) return this.$router.push("/panel/inventoryReport");
      window.location.href = "/panel/inventoryReport";
    },

    // ---------- UI Helpers (مثل قبلی) ----------
    formatInt(n) {
      return Math.round(Number(n || 0)).toLocaleString("fa-IR");
    },
    formatMoney0(n) {
      return Math.round(Number(n || 0)).toLocaleString("fa-IR");
    },
    formatExchange(n) {
      return Number(n || 0).toLocaleString("fa-IR");
    },
    formatTime(d) {
      try {
        return new Date(d).toLocaleTimeString("fa-IR");
      } catch (e) {
        return "—";
      }
    },
    colorHex(name) {
      const palette = ["#60a5fa", "#34d399", "#f472b6", "#a78bfa", "#fb7185", "#22d3ee", "#fbbf24", "#c084fc", "#4ade80", "#93c5fd"];
      const str = (name || "").toString();
      let seed = 0;
      for (let i = 0; i < str.length; i++) seed = (seed + str.charCodeAt(i) * (i + 1)) % 100000;
      return palette[seed % palette.length];
    },
    barStyle(it) {
      const total = this.totalValue || 0;
      const w = total ? (it.value / total) * 100 : 0;
      const c = this.colorHex(it.group);
      return {
        width: `${Math.min(100, Math.max(0, w))}%`,
        backgroundColor: c,
        boxShadow: `0 0 18px ${c}55`,
      };
    },
  },
};
</script>

<style scoped>
/* همون glass خودت */
.glass {
  background: rgba(2, 6, 23, 0.55);
  backdrop-filter: blur(22px);
  -webkit-backdrop-filter: blur(22px);
  border: 1px solid rgba(255, 255, 255, 0.10);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.55),
  inset 0 1px 0 rgba(255, 255, 255, 0.06);
  position: relative;
  isolation: isolate;
}

.glass::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(circle at 18% 12%, rgba(255, 255, 255, 0.08), transparent 45%),
  radial-gradient(circle at 90% 20%, rgba(99, 102, 241, 0.10), transparent 40%),
  linear-gradient(to bottom, rgba(255, 255, 255, 0.06), transparent 35%);
}

.glass > * {
  position: relative;
  z-index: 1;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: .45rem;
  padding: .4rem .6rem;
  border-radius: .75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.10);
  transition: .2s ease;
  white-space: nowrap;
}

.btn:hover {
  background: rgba(255, 255, 255, 0.10);
}

.btn:disabled {
  opacity: .5;
  cursor: not-allowed;
}

.btn--ghost {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.12);
}

.btn--ghost:hover {
  background: rgba(255, 255, 255, 0.06);
}

.btn--mini {
  padding: .35rem .55rem;
}

/* ticker */
.ticker {
  overflow: hidden;
}

.viewport {
  overflow: hidden;
}

.track {
  display: flex;
  gap: 10px;
  will-change: transform;
  transition: transform 380ms cubic-bezier(.2, .8, .2, 1);
}

/* ✅ ۳ ستون (ریسپانسیو) */
.miniCard {
  cursor: pointer;
  user-select: none;

  flex: 0 0 calc((100% - 20px) / 3); /* 3 cards + 2 gaps (10px*2) */
  border-radius: 1rem;
  padding: .75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.10);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transition: .2s ease;
}

.miniCard:hover {
  background: rgba(255, 255, 255, 0.07);
}

/* تبلت: ۲ ستون */
@media (max-width: 1023px) {
  .miniCard {
    flex-basis: calc((100% - 10px) / 2);
  }
}

/* موبایل: ۱ ستون */
@media (max-width: 639px) {
  .miniCard {
    flex-basis: 100%;
  }
}

.chipSmall {
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  padding: .15rem .45rem;
  border-radius: .75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.10);
}
</style>
